import { useVariablesDictionary } from "@babackapp/editor-blocks-library/variables-context";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/ui/components/select";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@babackapp/ui/components/input-group";
import { Tooltip, TooltipContent, TooltipTrigger } from "@babackapp/ui/components/tooltip";
import { cn } from "@babackapp/ui/lib/utils";
import * as LucideIcons from "lucide-react";
import { HelpCircleIcon, PencilIcon, SearchIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { groupVariables } from "../lib/variables";
import { useTemplatesEditorConfig } from "../context/templates-editor-config-context";
import { useTranslation } from "react-i18next";
import { ContextualTipsTrigger } from "@babackapp/ui/components/contextual-tips";


export function VariableDictionaryItemTitle(
  props: React.ComponentProps<"span">,
) {
  return (
    <span
      {...props}
      className={cn(
        "text-xs font-medium inline-flex gap-1 items-baseline",
        props.className,
      )}
    />
  );
}


export function VariableDictionaryPlaceholder({
  icon: Icon,
  onChange,
  defaultValue,
  ...props
}: React.ComponentProps<"input"> & {
  icon?: React.ComponentType<{ className: string }>;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState(defaultValue || "");
  const onClick = () => ref.current?.focus();

  useEffect(() => {
    setValue(defaultValue || "");
  }, [defaultValue]);

  const handleSave = () => {
    if (onChange) {
      onChange({ target: { value } } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSave();
      ref.current?.blur();
    }
  };

  return (
    <div className="inline-flex gap-1 items-center" onClick={onClick}>
      <div className="relative mt-2 border rounded-full bg-input items-center inline-flex gap-1 pl-1 focus-within:ring ring-ring">
        {Icon && <Icon className="size-3" />}
        <input
          ref={ref}
          placeholder="No value"
          onFocus={(e) => e.target.select()}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          {...props}
          className={cn(
            "field-sizing-content text-xs font-medium outline-none border-none ring-none pr-5",
            props.className,
          )}
        />
        <div className="absolute w-3 top-0 bottom-0 right-1 flex items-center justify-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <HelpCircleIcon className="size-3" />
            </TooltipTrigger>
            <TooltipContent className="max-w-[200px]">
              Cette valeur sert de preview pour l'éditeur de variables et ne
              sera pas utilisée dans le code généré.
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
      <PencilIcon className="size-3 translate-y-1 hidden group-hover:block" />
    </div>
  );
}


export function VariableDictionaryItemPath(
  props: React.ComponentProps<"span">,
) {
  return (
    <span
      {...props}
      className={cn("text-[10px] text-muted-foreground", props.className)}
    />
  );
}


export function VariableDictionaryItemDescription(
  props: React.ComponentProps<"p">,
) {
  return (
    <p
      {...props}
      className={cn("text-xs text-muted-foreground", props.className)}
    />
  );
}


export function VariableDictionaryItem(props: React.ComponentProps<"div">) {
  return (
    <div
      {...props}
      className={cn("bg-muted/60 rounded-lg p-2 group", props.className)}
    />
  );
}


export function VariablesDictionary(props: React.ComponentProps<"div">) {
  return (
    <div
      {...props}
      className={cn(
        "flex flex-col gap-2 overflow-visible pt-2 px-2",
        props.className,
      )}
    />
  );
}


export function VariablesDictionarySectionLabel({
  icon: iconName,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  icon?: string;
}) {
  const Icon = iconName
    ? (LucideIcons[
      (iconName.charAt(0).toUpperCase() +
        iconName.slice(1).replace(/-./g, (x) => x[1].toUpperCase()) +
        "Icon") as keyof typeof LucideIcons
      ] as React.ComponentType<{ className?: string }> | undefined)
    : null;

  return (
    <div
      {...props}
      className={cn(
        "text-xs font-medium text-muted-foreground flex items-center gap-1.5 mb-2",
        props.className,
      )}
    >
      {Icon && <Icon className="size-3.5" />}
      <span>{children}</span>
    </div>
  );
}


export function VariablesDictionarySection(props: React.ComponentProps<"div">) {
  return <div {...props} className={cn("flex flex-col", props.className)} />;
}


export function VariablesDictionarySectionContent(
  props: React.ComponentProps<"div">,
) {
  return (
    <div {...props} className={cn("flex flex-col gap-1", props.className)} />
  );
}


export function VariableDictionarySearch(props: React.ComponentProps<"input">) {
  return (
    <InputGroup>
      <InputGroupInput placeholder="Search..." {...props} />
      <InputGroupAddon>
        <SearchIcon />
      </InputGroupAddon>
    </InputGroup>
  );
}


export function VariablesDictionaryPanel() {
  const [search, setSearch] = useState("");
  const { t } = useTranslation("trackoffice");

  const searching = search.trim().length > 0;
  const { variables, groups } = useTemplatesEditorConfig();
  const editorVariablesEntries = useMemo(
    () => groupVariables(variables),
    [variables],
  );

  const matchingVars = useMemo(() => {
    if (!searching) return editorVariablesEntries;
    const searchLower = search.toLowerCase();
    return editorVariablesEntries.filter(([, vars]) =>
      vars.some(
        (definition) =>
          definition.name.toLowerCase().includes(searchLower) ||
          definition.description.toLowerCase().includes(searchLower),
      ),
    );
  }, [searching, search, editorVariablesEntries]);

  const { setVariable } = useVariablesDictionary();

  return (
    <ContextualTipsTrigger wrapped tips={[
      {
        id: "template-variables",
        title: t(
          "templatesEditor.tips.templateVariables.title",
          "Template's variables",
        ),
        description: t(
          "templatesEditor.tips.templateVariables.description",
          "Variables can be used to insert dynamic content in your email templates based on the parcel's or customer's data. Type '@' in any compatible input to see the list of available variables.",
        ),
      },
    ]}>
      <VariablesDictionary>
        <VariableDictionarySearch
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {matchingVars.map(([section, vars]) => {
          const editableVars = vars.filter((definition) => definition.editable);
          if (editableVars.length === 0) return null;

          const groupConfig = section in groups ? groups[section] : null;

          return (
            <VariablesDictionarySection key={section}>
              {!searching && groupConfig && (
                <VariablesDictionarySectionLabel icon={groupConfig.icon}>
                  {t(groupConfig.labelKey, section)}
                </VariablesDictionarySectionLabel>
              )}
              <VariablesDictionarySectionContent>
                {editableVars.map((definition) => (
                  <VariableDictionaryItem key={definition.id}>
                    <VariableDictionaryItemTitle>
                      {definition.name}
                      <VariableDictionaryItemPath>
                        ({definition.id})
                      </VariableDictionaryItemPath>
                      {definition.deprecated && (
                        <span className="text-[10px] text-orange-500 ml-1">(deprecated)</span>
                      )}
                    </VariableDictionaryItemTitle>
                    <VariableDictionaryItemDescription>
                      {definition.description}
                    </VariableDictionaryItemDescription>
                    <ContextualTipsTrigger wrapped tips={[
                      {
                        id: "template-variable-placeholder",
                        title: t(
                          "templatesEditor.tips.templateVariablePlaceholder.title",
                          "Placeholder value",
                        ),
                        description: t(
                          "templatesEditor.tips.templateVariablePlaceholder.description",
                          "This value will be inserted in the editor so you can preview the template with realistic data. It will not be used in the final email.",
                        ),
                      },
                    ]}>
                      {definition.options?.length ? (
                        <Select
                          defaultValue={String(definition.exampleValue)}
                          onValueChange={(value) =>
                            setVariable(definition.id, value)
                          }
                        >
                          <SelectTrigger className="mt-2 h-7 w-full text-xs bg-input">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {definition.options.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <VariableDictionaryPlaceholder
                          defaultValue={String(definition.exampleValue)}
                          onChange={(e) =>
                            setVariable(definition.id, e.target.value)
                          }
                        />
                      )}
                    </ContextualTipsTrigger>
                  </VariableDictionaryItem>
                ))}
              </VariablesDictionarySectionContent>
            </VariablesDictionarySection>
          );
        })}
      </VariablesDictionary>
    </ContextualTipsTrigger>
  );
}
